import { c as _c } from "react/compiler-runtime";
import { queryClient } from "@/config/react-query";
import { cx } from "@/lib/tailwind-utils";
import type { FirebaseProviderSummary } from "@/types/provider";
import { providerQueryKeys } from "@/api/provider/admin/provider-query";

/**
 * Sender avatar for a notification toast.
 *
 * Deliberately hook-free. The Toaster is mounted in the root layout, outside
 * the query/user providers, so `ProviderAvatar` would throw here — its
 * `useProviders`/`useQuery` have no context to read. The provider list is
 * already cached by the app, and `queryClient` is a module singleton, so the
 * signed image URL can be read directly with no extra request. When the list
 * has not loaded (or the provider has no photo) this falls back to initials,
 * which the notification payload always carries.
 */
export function NotificationAvatar(t0) {
  const $ = _c(23);
  const {
    providerId,
    firstName,
    lastName,
    size: t1
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  let t2;
  if ($[0] !== firstName) {
    t2 = firstName?.charAt(0) ?? "";
    $[0] = firstName;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== lastName) {
    t3 = lastName?.charAt(0) ?? "";
    $[2] = lastName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `${t2}${t3}`;
  let t5;
  if ($[4] !== t4) {
    t5 = t4.toUpperCase();
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const initials = t5;
  let imageUrl;
  if (providerId) {
    if ($[6] !== providerId) {
      for (const [, data] of queryClient.getQueriesData({
        queryKey: providerQueryKeys.lists()
      })) {
        const match = data?.find(p => p.id === providerId);
        const candidate = match?.profileImageUrl ?? (match?.profileImage && !match.profileImage.startsWith("gs://") ? match.profileImage : undefined);
        if (candidate) {
          imageUrl = candidate;
          break;
        }
      }
      $[6] = providerId;
      $[7] = imageUrl;
    } else {
      imageUrl = $[7];
    }
  }
  const t6 = `${size}px`;
  const t7 = `${size}px`;
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = {
      height: t6,
      width: t7
    };
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const dimensions = t8;
  if (imageUrl) {
    const t9 = initials || "Sender";
    let t10;
    if ($[11] !== dimensions || $[12] !== imageUrl || $[13] !== t9) {
      t10 = <img src={imageUrl} alt={t9} className="shrink-0 rounded-full object-cover" style={dimensions} />;
      $[11] = dimensions;
      $[12] = imageUrl;
      $[13] = t9;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    return t10;
  }
  const t9 = !initials;
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cx("flex shrink-0 items-center justify-center rounded-full", "bg-primary font-medium text-primary-foreground");
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = `${Math.floor(size * 0.4)}px`;
  let t12;
  if ($[16] !== dimensions || $[17] !== t11) {
    t12 = {
      ...dimensions,
      fontSize: t11
    };
    $[16] = dimensions;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== initials || $[20] !== t12 || $[21] !== t9) {
    t13 = <span aria-hidden={t9} className={t10} style={t12}>{initials}</span>;
    $[19] = initials;
    $[20] = t12;
    $[21] = t9;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  return t13;
}